import {
	FlatMenuItem,
	SecurityMap,
	MapOrEntry,
	MapEntry,
	EditorMap,
	EditorFlags,
	AccessLevels,
	AccessLevel,
} from '@siza-staff-care/shared/app-state';
import { KeyValuesPair } from '@siza-staff-care/shared/data';
import { camelCase } from 'lodash-es';

export const flattenMenuItems = (menuItems: KeyValuesPair[]): FlatMenuItem[] => {
	const build = (flatItems: FlatMenuItem[], menuItems: KeyValuesPair[]) => {
		menuItems.reduce((acc, item: KeyValuesPair) => {
			// Map the old menu names to the new ones.
			let key = item.key;
			if (key.startsWith('m:')) {
				key = key.substring(2).toLocaleLowerCase() + '-menu';
			}

			// Filter out the top-level menu items that are not actually menu items.
			const flatItem: FlatMenuItem = {
				key,
				name: item.value,
				accessLevel: item.value2,
			};
			acc.push(flatItem);

			if (item.children) {
				build(acc, item.children);
			}

			return acc;
		}, flatItems);
	};

	const resultItems: FlatMenuItem[] = [];
	build(resultItems, menuItems);

	return resultItems;
};

export const buildEditorMap = (items: KeyValuesPair[]) => {
	const map = {} as EditorMap;

	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		map[item.key] = item.flag as EditorFlags;
	}

	return map;
};

export const buildSecurityMapFromMenuItems = (items: KeyValuesPair[]): SecurityMap => {
	const map: SecurityMap = {};

	// Recursive function to process item and its children.
	const buildMap = (map: MapOrEntry, item: KeyValuesPair) => {
		const key = camelCase(/^m:/.test(item.key) ? item.key.substring(2) : item.key);

		let entry = map[key];
		if (!entry) {
			entry = map[key] = {} as MapEntry;
		}

		entry.level = item.value2;

		if (item.children) {
			for (const child of item.children) {
				buildMap(entry, child);
			}
		}
	};

	for (const item of items) {
		buildMap(map, item);
	}

	return map;
};

export const addSecurityItemsToSecurityMap = (map: SecurityMap, items: KeyValuesPair[]) => {
	map = map || {};

	const setLevelIfHigher = (existingLevel: AccessLevel, newLevel: AccessLevel) => {
		if (!existingLevel) {
			return newLevel;
		}

		// Priority order of levels - no access, read-only, full, mandatory.
		const levels = [AccessLevels.None, AccessLevels.ReadOnly, AccessLevels.Full, AccessLevels.Mandatory];
		const existingLevelIndex = levels.indexOf(existingLevel);
		const newLevelIndex = levels.indexOf(newLevel);

		// Set the new level only if lower (e.g. read-only or no access) than the current level.
		// The exception is Mandatory - if that's set, always set it, as it has to "shine through".
		if (newLevelIndex < existingLevelIndex || newLevel === AccessLevels.Mandatory) {
			return newLevel;
		}

		return existingLevel;
	};

	for (const item of items) {
		let child;
		let parent = map;

		const sanitisedKey = item.key.replaceAll('_', '.');
		const keys = sanitisedKey.split('.');
		for (const key of keys) {
			child = parent[key];
			if (!child) {
				parent[key] = child = {} as MapEntry;
			}

			parent = child;
		}

		child.level = setLevelIfHigher(child.level, item.value2 as AccessLevel);
	}

	return map;
};
